import { useEffect, useRef, useCallback } from 'react';
import { saveDraft } from '../services/offlineStorage.service';
import { isValidUID } from '../services/offlineStorage.service';

const AUTO_SAVE_DEBOUNCE_MS = 2000;

/**
 * Custom hook to auto-save evaluation form data to localStorage.
 * Runs on every form state change with a 2-second debounce.
 *
 * @param {string|null} draftKey - The localStorage key for this draft
 * @param {Object} formData - All form state to persist
 * @param {Function} onAutoSaved - Optional callback after auto-save
 *   Signature: (timestamp, savedData, trigger)
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - When false, auto-save to localStorage
 *   is suppressed (e.g. while online, where drafts are persisted on the server).
 */
export function useAutoSave(draftKey, formData, onAutoSaved, options = {}) {
  const { enabled = true } = options;
  const timerRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  /**
   * Validates form data before persisting to prevent storing untrusted data.
   * @param {Object} data - The form data to validate
   * @returns {boolean} - True if data appears safe to persist
   */
  const isFormDataSafe = useCallback((data) => {
    if (!data || typeof data !== 'object') {
      return false;
    }
    
    // Validate UIDs in the data if present
    if (data.selectedProspectUID && !isValidUID(data.selectedProspectUID)) {
      console.warn('Invalid selectedProspectUID in form data, skipping auto-save');
      return false;
    }
    
    // Validate event data
    if (data.selectedEvent?.id && (typeof data.selectedEvent.id !== 'number' && typeof data.selectedEvent.id !== 'string')) {
      console.warn('Invalid eventId in form data, skipping auto-save');
      return false;
    }
    
    return true;
  }, []);

  /**
   * Wrapped performSave that validates data before saving.
   */
  const performSaveWithValidation = useCallback((trigger = 'debounce') => {
    if (!draftKey) return;

    // When disabled (e.g. online), do not persist a local draft copy.
    if (!enabledRef.current) return;

    // SECURITY: Validate data before persisting
    if (!isFormDataSafe(formDataRef.current)) {
      console.warn('Form data failed validation, skipping auto-save');
      return;
    }
    
    saveDraft(draftKey, formDataRef.current);
    if (onAutoSaved) {
      onAutoSaved(Date.now(), formDataRef.current, trigger);
    }
  }, [draftKey, onAutoSaved, isFormDataSafe]);

  useEffect(() => {
    if (!draftKey || !enabled) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      performSaveWithValidation('debounce');
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [draftKey, formData, enabled, performSaveWithValidation]);

  // Save immediately on unmount (component closing / navigation)
  useEffect(() => {
    return () => {
      performSaveWithValidation('unmount');
    };
  }, [performSaveWithValidation]);

  // Return a manual force-save function
  return { forceSave: () => performSaveWithValidation('manual') };
}
